import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Mail,
  Download,
  Trash2,
  Loader2,
  Check,
  ShieldCheck,
  Package,
  ArrowRight,
  Eye,
  FileDown,
  Trash,
} from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LangContext";
import {
  requestDataAccess,
  fetchMyData,
  deleteMyData,
  exportMyDataUrl,
} from "@/lib/api";

const RequestForm = ({ isFr }) => {
  const { lang } = useLang();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await requestDataAccess(email, lang);
      setSent(true);
    } catch (_err) {
      // Even on backend error we show success to prevent enumeration. Just log.
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div
        data-testid="me-data-sent"
        className="text-center max-w-md mx-auto"
      >
        <div className="w-14 h-14 mx-auto rounded-full bg-[#3D2B1F] text-white flex items-center justify-center mb-6">
          <Check size={24} strokeWidth={1.6} />
        </div>
        <h2 className="font-serif-display text-2xl md:text-3xl text-[#1A1A1A] mb-3">
          {isFr ? "Vérifiez votre boîte mail." : "Check your inbox."}
        </h2>
        <p className="text-[15px] text-[#4a4a4a] leading-relaxed">
          {isFr
            ? "Si une adresse correspond à nos enregistrements, vous recevrez dans la minute un email contenant un lien sécurisé pour accéder à vos données. Le lien est valable 30 minutes."
            : "If an address matches our records, you'll receive within a minute a secure link to access your data. The link is valid for 30 minutes."}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="me-data-request-form" className="max-w-md mx-auto">
      <p className="text-[15px] text-[#4a4a4a] leading-relaxed mb-6 text-center">
        {isFr
          ? "Conformément au RGPD, vous pouvez à tout moment consulter, exporter ou supprimer vos données personnelles myFLOVEA. Entrez votre email — nous vous enverrons un lien sécurisé pour y accéder."
          : "Under the GDPR you can view, export or delete your myFLOVEA personal data at any time. Enter your email — we'll send you a secure access link."}
      </p>
      <label
        className="block text-xs mb-2"
        style={{ color: "#8B6343", letterSpacing: ".18em", textTransform: "uppercase" }}
      >
        Email
      </label>
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        data-testid="me-data-email"
        placeholder={isFr ? "votre.email@exemple.com" : "your.email@example.com"}
        className="w-full bg-transparent border-b border-[#3D2B1F]/40 focus:border-[#3D2B1F] outline-none py-3 text-[15px] mb-8 text-[#3D2B1F] placeholder:text-[#8a8378]"
      />
      <button
        type="submit"
        disabled={loading}
        data-testid="me-data-submit"
        className="w-full inline-flex items-center justify-center gap-3 bg-[#3D2B1F] text-white py-4 text-xs tracking-[0.22em] uppercase font-medium rounded-md hover:bg-[#1A1A1A] disabled:opacity-60 transition-colors"
      >
        {loading ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
        {isFr ? "Recevoir mon lien d'accès" : "Send me an access link"}
      </button>
      <p className="text-xs text-[#8a8378] mt-6 text-center leading-relaxed">
        {isFr
          ? "Aucune information ne fuit : si l'adresse n'existe pas, aucun email n'est envoyé."
          : "No data leakage: if the address doesn't exist, no email is sent."}
      </p>
    </form>
  );
};

const DataView = ({ token, isFr }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState(false);

  useEffect(() => {
    fetchMyData(token)
      .then(setData)
      .catch((err) => {
        const detail = err?.response?.data?.detail || (isFr ? "Lien invalide ou expiré." : "Invalid or expired link.");
        setError(detail);
      })
      .finally(() => setLoading(false));
  }, [token, isFr]);

  const doDelete = async () => {
    const ok = window.confirm(
      isFr
        ? "Confirmer la suppression de toutes vos données ? Cette action est irréversible. Vos commandes seront anonymisées (conservation comptable légale 10 ans) et votre inscription newsletter sera supprimée."
        : "Confirm deletion of all your data? This is irreversible. Your orders will be anonymized (legal 10-year accounting retention) and your newsletter subscription will be removed."
    );
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await deleteMyData(token);
      toast.success(
        isFr
          ? `Données supprimées · ${res.newsletter_removed} inscription · ${res.orders_anonymized} commande(s) anonymisée(s)`
          : `Data deleted · ${res.newsletter_removed} subscription · ${res.orders_anonymized} order(s) anonymized`
      );
      setDeleted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isFr ? "Erreur" : "Error"));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="me-data-loading" className="text-center">
        <Loader2 size={28} className="animate-spin mx-auto text-[#3D2B1F]" strokeWidth={1.4} />
        <p className="mt-6 text-sm text-[#4a4a4a]">
          {isFr ? "Chargement de vos données…" : "Loading your data…"}
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="me-data-error" className="text-center max-w-md mx-auto">
        <h2 className="font-serif-display text-2xl md:text-3xl text-[#1A1A1A] mb-3">
          {isFr ? "Lien indisponible" : "Link unavailable"}
        </h2>
        <p className="text-[15px] text-[#4a4a4a] leading-relaxed mb-8">{error}</p>
        <Link
          to="/me/data"
          data-testid="me-data-retry"
          className="inline-flex items-center gap-2 bg-[#3D2B1F] text-white px-6 py-3 text-xs tracking-[0.22em] uppercase rounded-md hover:bg-[#1A1A1A]"
        >
          {isFr ? "Demander un nouveau lien" : "Request a new link"}
          <ArrowRight size={14} />
        </Link>
      </div>
    );
  }

  if (deleted) {
    return (
      <div data-testid="me-data-deleted" className="text-center max-w-md mx-auto">
        <div className="w-14 h-14 mx-auto rounded-full bg-[#3D2B1F] text-white flex items-center justify-center mb-6">
          <Check size={24} strokeWidth={1.6} />
        </div>
        <h2 className="font-serif-display text-2xl md:text-3xl text-[#1A1A1A] mb-3">
          {isFr ? "Vos données ont été supprimées." : "Your data has been deleted."}
        </h2>
        <p className="text-[15px] text-[#4a4a4a] leading-relaxed mb-8">
          {isFr
            ? "Vos commandes ont été conservées de manière anonymisée pour les obligations comptables légales (10 ans). Vous ne recevrez plus aucune communication de notre part."
            : "Your orders were kept in anonymized form for the 10-year legal accounting obligation. You will no longer receive any communication from us."}
        </p>
        <Link to="/" className="text-sm text-[#3D2B1F] underline">
          {isFr ? "Retour à la boutique" : "Back to shop"}
        </Link>
      </div>
    );
  }

  const newsletter = data?.newsletter;
  const orders = data?.orders || [];

  return (
    <div data-testid="me-data-dashboard" className="max-w-2xl mx-auto">
      <div className="text-center mb-12">
        <div className="eyebrow mb-3 text-[#8B6343]">RGPD · {data?.email}</div>
        <h1 className="font-serif-display text-3xl md:text-4xl text-[#1A1A1A] leading-tight">
          {isFr ? "Voici tout ce que nous savons de vous." : "Everything we know about you."}
        </h1>
      </div>

      {/* Newsletter */}
      <section className="border border-[#dcd7ce] rounded-md p-6 mb-6 bg-white">
        <div className="flex items-center gap-3 mb-3">
          <Mail size={18} strokeWidth={1.4} className="text-[#8B6343]" />
          <h2 className="font-serif-display text-xl">{isFr ? "Newsletter" : "Newsletter"}</h2>
        </div>
        {newsletter ? (
          <div className="text-sm text-[#3D2B1F] leading-relaxed">
            <div>{isFr ? "Inscrit·e depuis :" : "Subscribed since:"} <strong>{newsletter.subscribed_at?.slice(0, 10)}</strong></div>
            <div>{isFr ? "Langue :" : "Language:"} {(newsletter.lang || "fr").toUpperCase()}</div>
            <div>{isFr ? "Source :" : "Source:"} {newsletter.source || "newsletter"}</div>
          </div>
        ) : (
          <p className="text-sm text-[#8a8378]">{isFr ? "Vous n'êtes pas abonné·e à la newsletter." : "You're not subscribed to the newsletter."}</p>
        )}
      </section>

      {/* Orders */}
      <section className="border border-[#dcd7ce] rounded-md p-6 mb-10 bg-white">
        <div className="flex items-center gap-3 mb-4">
          <Package size={18} strokeWidth={1.4} className="text-[#8B6343]" />
          <h2 className="font-serif-display text-xl">{isFr ? `Commandes (${orders.length})` : `Orders (${orders.length})`}</h2>
        </div>
        {orders.length === 0 ? (
          <p className="text-sm text-[#8a8378]">{isFr ? "Aucune commande enregistrée." : "No order on record."}</p>
        ) : (
          <ul className="divide-y divide-[#dcd7ce]">
            {orders.map((o) => (
              <li key={o.order_number} className="py-3 text-sm" data-testid={`me-data-order-${o.order_number}`}>
                <div className="flex justify-between gap-3">
                  <div>
                    <div className="font-serif-display text-base text-[#1A1A1A]">{o.order_number}</div>
                    <div className="text-xs text-[#8a8378]">{o.created_at?.slice(0, 10)} · {o.status}</div>
                  </div>
                  <div className="text-right font-serif-display text-base text-[#1A1A1A] whitespace-nowrap">
                    {Number(o.amount_total || 0).toFixed(2)} {o.currency || "EUR"}
                  </div>
                </div>
                {o.items_full?.length > 0 && (
                  <div className="text-xs text-[#4a4a4a] mt-1">
                    {o.items_full.map((it) => `${it.product_name} (${it.variant_name}) × ${it.quantity}`).join(" · ")}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <a
          href={exportMyDataUrl(token)}
          download
          data-testid="me-data-export"
          className="inline-flex items-center justify-center gap-3 bg-white border border-[#3D2B1F] text-[#3D2B1F] px-6 py-4 text-xs tracking-[0.22em] uppercase rounded-md hover:bg-[#F5EFE6]"
        >
          <Download size={14} />
          {isFr ? "Exporter mes données" : "Export my data"}
        </a>
        <button
          type="button"
          onClick={doDelete}
          disabled={deleting}
          data-testid="me-data-delete"
          className="inline-flex items-center justify-center gap-3 bg-[#3D2B1F] text-white px-6 py-4 text-xs tracking-[0.22em] uppercase rounded-md hover:bg-[#1A1A1A] disabled:opacity-60"
        >
          <Trash2 size={14} />
          {deleting ? (isFr ? "Suppression…" : "Deleting…") : (isFr ? "Supprimer mes données" : "Delete my data")}
        </button>
      </div>
      <p className="text-xs text-[#8a8378] mt-6 leading-relaxed text-center">
        {isFr
          ? "Les commandes sont conservées sous forme anonymisée pendant 10 ans (obligation comptable légale française). Toutes les autres données sont effacées immédiatement."
          : "Orders are kept in anonymized form for 10 years (French legal accounting obligation). All other data is erased immediately."}
      </p>
    </div>
  );
};

const InfoBlocks = ({ isFr }) => {
  const rights = isFr
    ? [
        { icon: Eye, title: "Droit d'accès (Art. 15)", text: "Consulter en clair toutes les données personnelles que nous détenons à votre sujet : commandes, newsletter, préférences." },
        { icon: FileDown, title: "Droit à la portabilité (Art. 20)", text: "Exporter vos données au format JSON structuré pour les transférer vers un autre service, à tout moment." },
        { icon: Trash, title: "Droit à l'effacement (Art. 17)", text: "Supprimer définitivement vos données. Les commandes sont anonymisées pour l'obligation comptable légale de 10 ans." },
        { icon: ShieldCheck, title: "Droit d'opposition & rectification", text: "Vous opposer à un traitement ou corriger une donnée inexacte en écrivant à contact@myflovea.com." },
      ]
    : [
        { icon: Eye, title: "Right of access (Art. 15)", text: "See in plain form every piece of personal data we hold about you: orders, newsletter, preferences." },
        { icon: FileDown, title: "Right to portability (Art. 20)", text: "Export your data in structured JSON to move it to another service, any time." },
        { icon: Trash, title: "Right to erasure (Art. 17)", text: "Permanently delete your data. Orders are anonymized for the 10-year legal accounting obligation." },
        { icon: ShieldCheck, title: "Right to object & rectify", text: "Object to a processing or correct inaccurate data by emailing contact@myflovea.com." },
      ];

  const steps = isFr
    ? [
        { n: "01", title: "Entrez votre email", text: "Nous vérifions qu'une adresse correspond à nos enregistrements. Si oui, nous vous envoyons un lien sécurisé." },
        { n: "02", title: "Recevez votre lien sécurisé", text: "Un email arrive dans la minute avec un lien unique valable 30 minutes, chiffré et à usage unique." },
        { n: "03", title: "Accédez, exportez ou supprimez", text: "Depuis votre tableau de bord privé, consultez, téléchargez au format JSON ou supprimez toutes vos données." },
      ]
    : [
        { n: "01", title: "Enter your email", text: "We check whether it matches an existing record. If so, we send you a secure link." },
        { n: "02", title: "Receive your secure link", text: "An email arrives within a minute with a unique 30-minute, encrypted, single-use link." },
        { n: "03", title: "View, export or delete", text: "From your private dashboard, view, download as JSON, or delete all your data." },
      ];

  return (
    <>
      {/* Explainer paragraph */}
      <div className="mt-10 max-w-2xl mx-auto text-center">
        <p className="text-[16px] leading-relaxed text-[#4a4a4a]">
          {isFr
            ? "Chez myFLOVEA, nous appliquons le Règlement Général sur la Protection des Données (RGPD, règlement UE 2016/679) à la lettre. Vous gardez le contrôle total sur vos informations personnelles — email, commandes, préférences newsletter — à chaque instant, gratuitement, sans justification à fournir."
            : "At myFLOVEA we apply the General Data Protection Regulation (GDPR, EU 2016/679) to the letter. You keep full control over your personal information — email, orders, newsletter preferences — at any time, free of charge, no reason required."}
        </p>
        <p className="mt-4 text-[15px] leading-relaxed text-[#4a4a4a]">
          {isFr
            ? "Aucune de vos données n'est vendue, revendue ou partagée à des fins publicitaires. Nos seuls sous-traitants sont : Stripe (paiement sécurisé), Resend (emails transactionnels) et Emergent (hébergement), tous certifiés RGPD."
            : "None of your data is sold, resold or shared for advertising. Our only sub-processors are: Stripe (secure payment), Resend (transactional email) and Emergent (hosting), all GDPR-certified."}
        </p>
      </div>

      {/* GDPR rights grid */}
      <div className="mt-14">
        <h2 className="font-serif-display text-2xl md:text-3xl text-[#1A1A1A] text-center mb-8">
          {isFr ? "Vos droits, garantis par la loi européenne" : "Your rights, guaranteed by EU law"}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {rights.map((r, i) => {
            const Icon = r.icon;
            return (
              <div
                key={i}
                data-testid={`me-data-right-${i}`}
                className="border border-[#dcd7ce] rounded-md p-5 bg-white"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Icon size={16} strokeWidth={1.5} className="text-[#8B6343]" />
                  <div className="font-serif-display text-[17px] text-[#1A1A1A]">{r.title}</div>
                </div>
                <p className="text-[14px] text-[#4a4a4a] leading-relaxed">{r.text}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* 3-step process */}
      <div className="mt-14">
        <h2 className="font-serif-display text-2xl md:text-3xl text-[#1A1A1A] text-center mb-8">
          {isFr ? "Comment ça fonctionne, en 3 étapes" : "How it works, in 3 steps"}
        </h2>
        <ol className="space-y-4">
          {steps.map((s, i) => (
            <li
              key={i}
              data-testid={`me-data-step-${i}`}
              className="flex gap-5 items-start border-l-2 border-[#8B6343] pl-5"
            >
              <span className="font-serif-display text-2xl text-[#8B6343] shrink-0">{s.n}</span>
              <div>
                <div className="font-serif-display text-lg text-[#1A1A1A]">{s.title}</div>
                <p className="mt-1 text-[14px] text-[#4a4a4a] leading-relaxed">{s.text}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Retention block */}
      <div className="mt-14 bg-[#F5EFE6] border border-[#dcd7ce] rounded-md p-8">
        <h2 className="font-serif-display text-2xl text-[#1A1A1A] mb-4">
          {isFr ? "Ce que nous conservons, et pourquoi" : "What we keep, and why"}
        </h2>
        <ul className="space-y-3 text-[14px] text-[#3D2B1F] leading-relaxed">
          <li>
            <strong>{isFr ? "Commandes : 10 ans (anonymisées après suppression)." : "Orders: 10 years (anonymized after deletion)."}</strong>{" "}
            {isFr
              ? "Obligation comptable et fiscale française (Art. L123-22 Code de commerce)."
              : "French accounting and tax obligation (Art. L123-22 Commercial Code)."}
          </li>
          <li>
            <strong>{isFr ? "Newsletter : jusqu'à désinscription." : "Newsletter: until unsubscription."}</strong>{" "}
            {isFr
              ? "Consentement libre et révocable à tout moment (Art. 7 RGPD)."
              : "Free consent, revocable any time (Art. 7 GDPR)."}
          </li>
          <li>
            <strong>{isFr ? "Emails de contact : 3 ans maximum." : "Contact emails: 3 years max."}</strong>{" "}
            {isFr
              ? "Traitement des demandes SAV et suivi de qualité (base légale : intérêt légitime)."
              : "Processing after-sales requests and quality follow-up (legal basis: legitimate interest)."}
          </li>
          <li>
            <strong>{isFr ? "Cookies : 13 mois maximum." : "Cookies: 13 months max."}</strong>{" "}
            {isFr
              ? "Conformément aux recommandations CNIL. Vous pouvez tout refuser dans notre bannière ou dans « Gérer mes cookies »."
              : "In line with CNIL guidelines. You can refuse everything via our banner or in \"Manage my cookies\"."}
          </li>
        </ul>
        <p className="mt-6 text-xs text-[#8a8378] leading-relaxed">
          {isFr
            ? "En cas de réclamation, vous avez le droit d'adresser une plainte à la CNIL (Commission Nationale de l'Informatique et des Libertés) — 3 Place de Fontenoy, 75007 Paris — ou en ligne sur cnil.fr."
            : "In case of complaint, you have the right to lodge a claim with the CNIL (French data protection authority) — 3 Place de Fontenoy, 75007 Paris — or online at cnil.fr."}
        </p>
      </div>
    </>
  );
};

const MyData = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { lang } = useLang();
  const isFr = lang === "fr";

  return (
    <section
      data-testid="me-data-page"
      className="max-w-3xl mx-auto px-6 sm:px-10 py-16 md:py-24"
    >
      <Seo
        lang={lang}
        path="/me/data"
        title={isFr ? "Mes données (RGPD)" : "My data (GDPR)"}
        description={
          isFr
            ? "Vos droits RGPD chez myFLOVEA : consulter, exporter au format JSON ou supprimer vos données personnelles en 3 étapes. Accès sécurisé par email, chiffré, valable 30 minutes."
            : "Your GDPR rights at myFLOVEA: view, export as JSON or delete your personal data in 3 steps. Secure email access, encrypted, valid for 30 minutes."
        }
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: isFr ? "Mes données (RGPD) — myFLOVEA" : "My data (GDPR) — myFLOVEA",
            url: "https://myflovea.com/me/data",
            about: {
              "@type": "Thing",
              name: isFr ? "Droits RGPD des consommateurs" : "GDPR consumer rights",
              sameAs: "https://gdpr-info.eu/",
            },
            publisher: {
              "@type": "Organization",
              name: "myFLOVEA",
              legalName: "EM GROUP SASU",
            },
          })}
        </script>
      </Helmet>
      {!token && (
        <>
          <div className="text-center mb-12">
            <ShieldCheck size={32} strokeWidth={1.4} className="mx-auto text-[#8B6343] mb-4" />
            <div className="eyebrow mb-3 text-[#8B6343]">RGPD · Données personnelles</div>
            <h1 className="font-serif-display text-3xl md:text-5xl text-[#1A1A1A] leading-tight">
              {isFr ? "Vos données vous appartiennent." : "Your data belongs to you."}
            </h1>
          </div>
          <RequestForm isFr={isFr} />
          <InfoBlocks isFr={isFr} />
        </>
      )}
      {token && <DataView token={token} isFr={isFr} />}
    </section>
  );
};

export default MyData;
